import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useGetSubscriptionNotificationSettingQuery,
  useSaveSubscriptionNotificationSettingMutation,
} from "../../../../store/queries/Subscription";

// The blank slate. Shared between formik's starting point and Reset so the two cannot drift.
const defaultValues = {
  days_before: 3,
  title: "",
  alias_title: "",
  message: "",
  alias_message: "",
  status: 1,
};

const useNotificationSetting = (handleClose) => {
  const [isSaving, setIsSaving] = useState(false);

  const { data: settingData = {}, isFetching } =
    useGetSubscriptionNotificationSettingQuery();
  const [saveSetting] = useSaveSubscriptionNotificationSettingMutation();

  const emptyToNull = (value, original) => (original === "" ? null : value);

  const validation = Yup.object({
    days_before: Yup.number()
      .transform(emptyToNull)
      .required("Enter how many days before expiry")
      .integer("Enter a whole number")
      .min(1, "At least 1 day")
      .max(365, "At most 365 days"),
    title: Yup.string().trim().required("Enter a title").max(150),
    alias_title: Yup.string().nullable().max(150),
    message: Yup.string().trim().required("Enter a message").max(500),
    alias_message: Yup.string().nullable().max(500),
  });

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: validation,
    onSubmit: async (values) => {
      setIsSaving(true);

      const response = await saveSetting({
        days_before: Number(values?.days_before),
        title: values?.title,
        alias_title: values?.alias_title,
        message: values?.message,
        alias_message: values?.alias_message,
        status: Number(values?.status),
      });

      if (response?.data?.status_code === 200) {
        toast.success(`Expiry warning saved`);
        setIsSaving(false);
        handleClose();
        return;
      }

      toast.error(
        response?.error?.data?.message ??
          response?.data?.message ??
          `Unable to save the expiry warning`
      );
      setIsSaving(false);
    },
  });

  // A null setting is the ordinary starting state - expiry warnings have simply never been
  // configured - so the form keeps its defaults rather than showing empty required fields.
  useEffect(() => {
    const setting = settingData?.data;
    if (setting) {
      formik?.setValues({
        days_before: setting?.days_before ?? defaultValues.days_before,
        title: setting?.title ?? "",
        alias_title: setting?.alias_title ?? "",
        message: setting?.message ?? "",
        alias_message: setting?.alias_message ?? "",
        status: setting?.status ?? defaultValues.status,
      });
    }
    //eslint-disable-next-line
  }, [settingData]);

  /**
   * Blanks the fields so a new warning can be typed from scratch, rather than editing over
   * the saved one. Local only - the stored setting is untouched until Save, so a Reset the
   * user did not mean costs them nothing but a reopen.
   *
   * resetForm also clears touched/errors, so the emptied required fields do not immediately
   * light up red before anything has been typed.
   */
  const handleReset = () => {
    formik.resetForm({ values: defaultValues });
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  return { formik, isSaving, isFetching, getFieldError, handleReset };
};

export default useNotificationSetting;
